import { isDefined } from '@sgme/fp';
import type {
  SecondaryPostEventsLivePersistedTabContent,
  SecondaryQuotesLiveFormPersistedTabContent,
} from 'apiConnector/tabs/secondaryMarket.tabs.model';
import { formatToPriceType } from 'state/secondaryMarket/live/live.selector';
import { formInputSelectorsFactory } from 'state/secondaryMarket/shared/form/input/input.selector';
import { fromSecondaryPostEventsWorkspace, fromSecondaryQuotesWorkspace } from 'state/selectors';
import type { AppState } from 'state/state.model';
import { fromBcsProductKey } from 'state/workspaces/secondary-market/quotes/secondartMarketQuotes.shared';

function select(state: AppState, tabId: string) {
  return state.secondaryMarket.live.form.input[tabId];
}

export const liveFormInputSelectors = formInputSelectorsFactory('live', {
  getFormInputHasUpfrontFee,
  getFormInputCustomizedXMarginAdjustment,
  getFormInputCustomizedXMarginAdjustmentFormatted,
  getFormInputCustomizedTradingPriceAdjustment,
  getFormInputCustomizedTradingPriceAdjustmentFormatted,
  getFormInputCustomizedUpfront,
  getFormInputCustomizedUpfrontFormatted,
  getFormInputSalesMargin,
  getFormInputTraceReporting,
  getFormTabContentToPersist,
  getAmendFormTabContentToPersist,
  isPriceAdjusted,
});

function getFormInputHasUpfrontFee(state: AppState, tabId: string) {
  return select(state, tabId)?.hasUpfrontFee;
}

function getFormInputCustomizedXMarginAdjustment(state: AppState, tabId: string) {
  return select(state, tabId)?.customizedXMarginAdjustment;
}

function getFormInputCustomizedXMarginAdjustmentFormatted(state: AppState, tabId: string) {
  const value = getFormInputCustomizedXMarginAdjustment(state, tabId);
  return formatToPriceType(state, tabId, value);
}

function getFormInputCustomizedTradingPriceAdjustment(state: AppState, tabId: string) {
  return select(state, tabId)?.customizedTradingPriceAdjustment;
}

function isPriceAdjusted(state: AppState, tabId: string) {
  return (
    isDefined(getFormInputCustomizedTradingPriceAdjustment(state, tabId)) ||
    isDefined(getFormInputCustomizedXMarginAdjustment(state, tabId))
  );
}

function getFormInputCustomizedTradingPriceAdjustmentFormatted(state: AppState, tabId: string) {
  const value = getFormInputCustomizedTradingPriceAdjustment(state, tabId);
  return formatToPriceType(state, tabId, value);
}

function getFormInputCustomizedUpfront(state: AppState, tabId: string) {
  return select(state, tabId)?.customizedUpfront;
}

function getFormInputCustomizedUpfrontFormatted(state: AppState, tabId: string) {
  const value = select(state, tabId)?.customizedUpfront;
  return formatToPriceType(state, tabId, value);
}
function getFormInputSalesMargin(state: AppState, tabId: string) {
  return select(state, tabId)?.salesMargin;
}

function getFormInputTraceReporting(state: AppState, tabId: string) {
  return select(state, tabId)?.traceReporting ?? false;
}

function getFormTabContentToPersist(
  appState: AppState,
  tabId: string,
): SecondaryQuotesLiveFormPersistedTabContent | undefined {
  const state = select(appState, tabId);
  if (state === undefined) {
    return undefined;
  }
  const { client, clientType, ...form } = state;
  const bcskey = fromSecondaryQuotesWorkspace.getProductKey(appState, tabId);

  return {
    client,
    clientType: clientType ?? 'Other',
    ...form,
    bcsProductKey: bcskey,
    workspace: 'secondaryMarketQuotes',
    tabType: 'live',
  };
}

function getAmendFormTabContentToPersist(
  appState: AppState,
  tabId: string,
): SecondaryPostEventsLivePersistedTabContent | undefined {
  const state = select(appState, tabId);
  if (state === undefined) {
    return undefined;
  }
  const { client, clientType, ...form } = state;
  const bcskey = fromBcsProductKey(fromSecondaryPostEventsWorkspace.getBcsProductKey(appState, tabId))!;
  return {
    client,
    clientType: clientType ?? 'Other',
    ...form,
    bcsProductKey: bcskey,
    workspace: 'secondaryMarketPostEvents',
    tabType: 'live',
  };
}
export type LiveInputFormSelectors = typeof liveFormInputSelectors;
